import useTodaysAppointments from '@hooks/useTodaysAppointments';
import { Room, LocalDataTrack } from 'twilio-video';
import { useTestPatientRoom } from '@hooks/useTestPatientRoom';
import useUser from '@vl-core/hooks/useUser';
import { useEffect, useCallback } from 'react';
import { CallManager } from '@helpers/CallManager';
import { useTestCall } from './PatientTestCallManager';
import useAvatar from 'vl-common/src/hooks/useAvatar';
import useAudio from '@hooks/useAudio';
import useRouter from '@vl-core/useRouter';
import { CloseCircleOutlined, PhoneOutlined } from 'antd/icons';
import { confirmationModal } from 'vl-common/src/components/Modals';
import { type Modal } from 'antd';
import i18n from 'vl-common/src/lib/i18n';

const t = i18n.getFixedT(null, 'main');

type Appointment = ReturnType<typeof useTodaysAppointments>['appointments'][0];

interface RingerModalCallHandlers {
  decline: () => Promise<void>;
  pause: () => void;
  startCall: () => Promise<void>;
}

export function getRingerModal(
  appointment: Appointment | undefined,
  appointmentStatus: string,
  clinicianAvatar: string,
  callHandlers: RingerModalCallHandlers,
  push: ReturnType<typeof useRouter>['push']
) {
  const isTestCall = !appointment;

  return confirmationModal({
    zIndex: 2147483003,
    wrapClassName: 'clinician-calling-modal',
    title: isTestCall ? 'Test Clinician is calling' : `${appointment.clinician} is calling`,
    open: appointmentStatus === 'calling',
    content: (
      <div
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderBottom: '1px solid grey',
          paddingTop: 40,
          paddingBottom: 40
        }}
      >
        <iframe
          title="video call ringer"
          src="/static/silence.mp3"
          allow="autoplay"
          id="audio"
          style={{ display: 'none' }}
        />
        <img
          style={{ width: 150, height: 150, borderRadius: 75, marginInline: 'auto', objectFit: 'cover' }}
          src={clinicianAvatar || (isTestCall ? '/static/images/userPersonas/clin_headshot.png' : '')}
          alt={t('Avatar')}
        />
      </div>
    ),
    okText: 'Answer',
    cancelButtonProps: { style: { color: 'red' }, icon: <CloseCircleOutlined />, 'data-testid': 'decline-call' },
    okButtonProps: { icon: <PhoneOutlined />, 'data-testid': 'answer-call' },
    onCancel: (close) => {
      callHandlers.decline().then();
      callHandlers.pause();
      close();
    },
    onOk: (close) => {
      callHandlers.pause();
      close();
      callHandlers.startCall();

      if (isTestCall) {
        setTimeout(() => {
          push('/patients/test-call');
        }, 0);
      } else {
        const encryptid = Buffer.from(`${appointment.appointment_id}`).toString('base64');
        push(`/patients/session?req=${encryptid}&on=${appointment.call_type_code}`);
      }
    }
  });
}

const PatientsRinger = ({
  appointment,
  room,
  dataTrack,
  signalingManager
}: {
  appointment?: Appointment;
  room: Room;
  dataTrack: LocalDataTrack;
  signalingManager: CallManager | undefined;
}) => {
  const { isTestCallEnabled, setIsTestCallEnabled, setHasAcceptedTestCall } = useTestCall();
  const clinicianAvatar = useAvatar(appointment?.clinician_user_guid || '');
  const [, play, pause] = useAudio('/static/telephone-ring.mp3');
  const { push } = useRouter();

  const handleDecline = useCallback(async () => {
    try {
      if (!signalingManager) {
        setIsTestCallEnabled(false);
        return;
      }
      Promise.resolve(signalingManager.declineCall()).catch((error: unknown) => {
        console.error('Failed to decline call:', error);
      });
    } catch (error) {
      console.error('Failed to decline call:', error);
    } finally {
      setIsTestCallEnabled(false);
    }
  }, [signalingManager, setIsTestCallEnabled]);

  const handleStartCall = useCallback(async () => {
    if (!appointment && !isTestCallEnabled) {
      console.warn('Test call accepted but context was already disabled.');
      return;
    }
    Promise.resolve(signalingManager?.acceptOffer()).catch((error: unknown) => {
      console.error('Failed to accept offer:', error);
    });
    if (!appointment) {
      setHasAcceptedTestCall(true);
    }
  }, [appointment, isTestCallEnabled, signalingManager, setHasAcceptedTestCall]);

  useEffect(() => {
    let modal: ReturnType<typeof Modal.confirm>;
    if (!room || !dataTrack || !signalingManager) return;

    signalingManager.setHandlers({
      onCallOffer: () => {
        if (!appointment && !isTestCallEnabled) return;

        play();
        modal = getRingerModal(
          appointment,
          'calling',
          clinicianAvatar ?? '',
          {
            decline: handleDecline,
            pause,
            startCall: handleStartCall
          },
          push
        );
      }
    });

    return () => {
      pause();
      modal?.destroy();
    };
  }, [
    room,
    dataTrack,
    signalingManager,
    push,
    play,
    pause,
    isTestCallEnabled,
    appointment,
    clinicianAvatar,
    handleDecline,
    handleStartCall
  ]);

  return null;
};

// dummy component to manage a single appointment (that doesn't render)
export const ManageAppointment = ({ appointment }: { appointment?: Appointment }) => {
  const user = useUser();
  const { setRoom, setSignalingManager } = useTestCall();

  const { room, dataTrack, signalingManager } = useTestPatientRoom(user.user_guid);

  useEffect(() => {
    if (room) {
      setRoom(room);
    }
    return () => setRoom(undefined);
  }, [room, setRoom]);

  useEffect(() => {
    if (signalingManager) {
      setSignalingManager(signalingManager);
    }
    return () => setSignalingManager(undefined);
  }, [signalingManager, setSignalingManager]);

  if (!room || !dataTrack) return null;

  return (
    <PatientsRinger appointment={appointment} room={room} dataTrack={dataTrack} signalingManager={signalingManager} />
  );
};

function isViable(appointment: Appointment) {
  if (appointment.appointment_completed) return false;

  return appointment.call_type_code === 'VIDEO';
}

// Loads today's appointments; for each viable appointment connects to its room and displays the patient appointment ringer
export const RoomCacheManager = () => {
  const { appointments } = useTodaysAppointments(5 * 60 * 1000);

  return (
    <div>
      {appointments.filter(isViable).map((a) => (
        <ManageAppointment key={a.appointment_id} appointment={a} />
      ))}
    </div>
  );
};
